var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Vue, Component } from "vue-facing-decorator";
import { State, Action } from "vuex-facing-decorator";
import { BackgroundTasksState } from "@/app/store/modules/background-tasks.state";
import BackgroundTaskDetailsModal from "@/app/vues/components/background-task-details-modal.vue";
import { BackgroundTaskStatus, isFinishedStatus, parseBackgroundTaskDetails } from "@/app/models/background-task";
const tasksNamespace = { namespace: "backgroundTasksModule" };
/**
 * The Background tasks panel: the signed-in user's long-running tasks. The list and its live updates
 * live in the backgroundTasks store, shared with the sidebar icon that reopens the panel.
 *
 * Generic by design: it knows nothing about what a task does. Every task reports the same things
 * (overall progress, the current item and its step, an outcome, per-item details as
 * BackgroundTaskDetails) and can be cancelled the same way. New kinds of background job need no
 * changes here.
 */
let ProgressPanelVue = class ProgressPanelVue extends Vue {
    constructor() {
        super(...arguments);
        this.shifted = false;
        // Above the page and menus, below Bootstrap modals (1055) so the details dialog covers it.
        this.zIndex = 1040;
        this.userMoved = false;
        this.now = Date.now();
        this.selectedMessageId = null;
        this.statuses = BackgroundTaskStatus;
        this.timer = null;
        this.observer = null;
        // Drag state
        this.drag = { active: false, offsetX: 0, offsetY: 0 };
        this.pos = { x: null, y: null };
    }
    created() {
        this.startTasks();
        window.addEventListener("mousemove", this.onDrag);
        window.addEventListener("mouseup", this.stopDrag);
    }
    mounted() {
        this.observeNotifications();
        this.timer = window.setInterval(() => {
            this.now = Date.now();
        }, 1000);
    }
    beforeUnmount() {
        var _a;
        // Mounted only while signed in: going away means signing out.
        this.stopTasks();
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
        window.removeEventListener("mousemove", this.onDrag);
        window.removeEventListener("mouseup", this.stopDrag);
    }
    get panelStyle() {
        if (this.pos.x !== null && this.pos.y !== null) {
            return {
                zIndex: this.zIndex,
                left: `${this.pos.x}px`,
                top: `${this.pos.y}px`,
                right: "auto",
                bottom: "auto",
                maxWidth: "500px"
            };
        }
        return {
            zIndex: this.zIndex,
            right: this.shifted ? "340px" : "20px",
            bottom: "20px",
            left: "auto",
            top: "auto",
            maxWidth: "500px"
        };
    }
    get finishedCount() {
        return this.tasks.messages.filter(m => this.isFinished(m)).length;
    }
    get selectedMessage() {
        var _a;
        return (_a = this.tasks.messages.find(m => m.id === this.selectedMessageId)) !== null && _a !== void 0 ? _a : null;
    }
    observeNotifications() {
        const checkAndShift = () => {
            if (this.userMoved)
                return;
            const notifGroup = document.querySelector(".vue-notification-group");
            let hasNotifications = false;
            if (notifGroup) {
                const span = notifGroup.querySelector("span");
                if (span && span.children.length > 0) {
                    hasNotifications = true;
                }
            }
            this.shifted = hasNotifications;
        };
        this.observer = new MutationObserver(checkAndShift);
        this.observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(checkAndShift, 100);
    }
    isCancelling(msg) {
        return !this.isFinished(msg) && this.tasks.cancellingIds.includes(msg.id);
    }
    cancelTask(msg) {
        const text = `Cancel "${msg.processName}"? Anything already finished is kept. What is in progress is stopped and undone, and anything not started yet is skipped.`;
        this.$confirmModal.show(text, { title: "Cancel background task" })
            .then((confirmed) => {
            if (confirmed)
                this.cancel(msg.id);
        });
    }
    isFinished(msg) {
        return isFinishedStatus(msg.status);
    }
    hasDetails(msg) {
        var _a, _b;
        return ((_b = (_a = parseBackgroundTaskDetails(msg.details)) === null || _a === void 0 ? void 0 : _a.items.length) !== null && _b !== void 0 ? _b : 0) > 0;
    }
    openDetails(msg) {
        this.selectedMessageId = msg.id;
        if (this.isFinished(msg))
            this.acknowledge(msg.id);
        this.$nextTick(() => { var _a; return (_a = this.$refs.taskDetails) === null || _a === void 0 ? void 0 : _a.open(this.isFinished(msg) && !!msg.errorMessage); });
    }
    stepPercent(msg) {
        return msg.stepTotal ? Math.min(100, Math.floor(100 * (msg.stepCurrent || 0) / msg.stepTotal)) : 0;
    }
    statusClass(status) {
        if (status === BackgroundTaskStatus.Completed)
            return "list-group-item-success";
        if (status === BackgroundTaskStatus.CompletedWithErrors)
            return "list-group-item-warning";
        if (status === BackgroundTaskStatus.Error)
            return "list-group-item-danger";
        if (status === BackgroundTaskStatus.Cancelled)
            return "list-group-item-secondary";
        return "";
    }
    formatDuration(start, end) {
        const startDate = new Date(start);
        const endDate = end ? new Date(end) : new Date(this.now);
        let seconds = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / 1000));
        const h = Math.floor(seconds / 3600);
        seconds %= 3600;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
    }
    // --- Drag logic ---
    startDrag(e) {
        if (e.target.closest(".panel-actions"))
            return;
        this.drag.active = true;
        const panel = this.$refs.panel;
        const rect = panel.getBoundingClientRect();
        this.drag.offsetX = e.clientX - rect.left;
        this.drag.offsetY = e.clientY - rect.top;
        this.bringToFront();
        this.userMoved = true;
    }
    onDrag(e) {
        if (!this.drag.active)
            return;
        const panel = this.$refs.panel;
        if (!panel)
            return;
        const maxX = window.innerWidth - panel.offsetWidth;
        const maxY = window.innerHeight - panel.offsetHeight;
        this.pos.x = Math.max(0, Math.min(e.clientX - this.drag.offsetX, maxX));
        this.pos.y = Math.max(0, Math.min(e.clientY - this.drag.offsetY, maxY));
    }
    stopDrag() {
        this.drag.active = false;
    }
    bringToFront() {
        this.zIndex = Math.min(this.zIndex + 1, 1050);
    }
};
__decorate([
    State("backgroundTasksModule"),
    __metadata("design:type", typeof (_a = typeof BackgroundTasksState !== "undefined" && BackgroundTasksState) === "function" ? _a : Object)
], ProgressPanelVue.prototype, "tasks", void 0);
__decorate([
    Action("start", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "startTasks", void 0);
__decorate([
    Action("stop", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "stopTasks", void 0);
__decorate([
    Action("closePanel", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "closePanel", void 0);
__decorate([
    Action("dismiss", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "dismiss", void 0);
__decorate([
    Action("dismissFinished", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "dismissFinished", void 0);
__decorate([
    Action("cancel", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "cancel", void 0);
__decorate([
    Action("acknowledge", tasksNamespace),
    __metadata("design:type", Object)
], ProgressPanelVue.prototype, "acknowledge", void 0);
ProgressPanelVue = __decorate([
    Component({
        name: "ProgressPanelVue",
        components: { BackgroundTaskDetailsModal }
    })
], ProgressPanelVue);
export default ProgressPanelVue;
